/**
 * Theme, font, and font-size preferences for the entire system
 * Persists to localStorage and applies via CSS variables on document
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { THEME_OPTIONS, getThemeDefinition, getThemeVars, VAR_KEYS } from '../config/themeDefinitions';

const STORAGE_KEY = '360feedback-theme';

const FONT_OPTIONS = [
  { id: 'theme', label: 'Theme default', family: null },
  { id: 'Inter', label: 'Inter', family: "'Inter', ui-sans-serif, system-ui, sans-serif" },
  { id: 'Roboto', label: 'Roboto', family: "'Roboto', ui-sans-serif, system-ui, sans-serif" },
  { id: 'Open Sans', label: 'Open Sans', family: "'Open Sans', ui-sans-serif, system-ui, sans-serif" },
  { id: 'Lato', label: 'Lato', family: "'Lato', ui-sans-serif, system-ui, sans-serif" },
  { id: 'Poppins', label: 'Poppins', family: "'Poppins', ui-sans-serif, system-ui, sans-serif" },
];

const FONT_SIZE_OPTIONS = [
  { id: 'small', label: 'Small', base: '13px', scale: 0.87 },
  { id: 'medium', label: 'Medium', base: '15px', scale: 1 },
  { id: 'large', label: 'Large', base: '17px', scale: 1.13 },
];

const DENSITY_OPTIONS = [
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'compact', label: 'Compact' },
];

const defaultState = {
  theme: 'professional',
  fontFamily: 'theme',
  fontSize: 'medium',
  density: 'comfortable',
};

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        theme: THEME_OPTIONS.some((t) => t.id === parsed.theme) ? parsed.theme : defaultState.theme,
        fontFamily: FONT_OPTIONS.some((f) => f.id === parsed.fontFamily) ? parsed.fontFamily : defaultState.fontFamily,
        fontSize: FONT_SIZE_OPTIONS.some((s) => s.id === parsed.fontSize) ? parsed.fontSize : defaultState.fontSize,
        density: DENSITY_OPTIONS.some((d) => d.id === parsed.density) ? parsed.density : defaultState.density,
      };
    }
  } catch (_) {}
  return { ...defaultState };
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [state, setState] = useState(loadSaved);

  const save = useCallback((next) => {
    setState((prev) => {
      const merged = { ...prev, ...next };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      } catch (_) {}
      return merged;
    });
  }, []);

  const setTheme = useCallback((theme) => save({ theme, fontFamily: 'theme' }), [save]);
  const setFontFamily = useCallback((fontFamily) => save({ fontFamily }), [save]);
  const setFontSize = useCallback((fontSize) => save({ fontSize }), [save]);
  const setDensity = useCallback((density) => save({ density }), [save]);
  const resetAppearance = useCallback(() => save(defaultState), [save]);

  useEffect(() => {
    const doc = document.documentElement;
    const theme = state.theme;
    const fontOpt = FONT_OPTIONS.find((f) => f.id === state.fontFamily) || FONT_OPTIONS[0];
    const sizeOpt = FONT_SIZE_OPTIONS.find((s) => s.id === state.fontSize) || FONT_SIZE_OPTIONS[1];

    doc.style.setProperty('--font-size-base', sizeOpt.base);
    doc.style.setProperty('--font-scale', String(sizeOpt.scale));

    const vars = getThemeVars(theme);
    if (vars) {
      VAR_KEYS.forEach((key) => doc.style.setProperty(key, vars[key] || ''));
    }
    if (fontOpt.family) doc.style.setProperty('--font-family', fontOpt.family);

    const definition = getThemeDefinition(theme);
    doc.setAttribute('data-theme', theme);
    doc.setAttribute('data-density', state.density);
    doc.classList.remove('theme-light', 'theme-dark');
    doc.classList.add(`theme-${definition.mode}`);
  }, [state.theme, state.fontFamily, state.fontSize, state.density]);

  const value = {
    theme: state.theme,
    fontFamily: state.fontFamily,
    fontSize: state.fontSize,
    density: state.density,
    setTheme,
    setFontFamily,
    setFontSize,
    setDensity,
    resetAppearance,
    FONT_OPTIONS,
    FONT_SIZE_OPTIONS,
    DENSITY_OPTIONS,
    THEME_OPTIONS,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
